// Web login helpers (Kakao JS SDK + Sign in with Apple JS).
//
// KEYS TO FILL (client-side public values; safe to commit for a web app):
//   - KAKAO_JS_KEY: gogo Kakao app (ID 71566) JavaScript key — copy from
//     Kakao Developers > 내 애플리케이션 > 앱 키 (NOT the native key 54978…).
//   - APPLE_SERVICES_ID: an Apple "Services ID" (e.g. space.go-go.web) configured
//     for Sign in with Apple, with go-go.space as a domain + return URL.
// CONSOLE REGISTRATION STILL NEEDED (see reports):
//   - Kakao: 카카오 로그인 > Redirect URI = `${origin}/auth/kakao/callback`
//   - Apple: Services ID return URL = `${origin}/auth/apple/callback`
//
// BACKEND SEAM (does not exist yet):
//   Kakao web can only obtain an authorization CODE client-side (Kakao.Auth.login
//   returning an access_token is deprecated). The code must be exchanged for a
//   Kakao access_token server-side (REST key). So the web Kakao flow needs a
//   backend route — see `authenticateKakaoWeb` in src/api/writes.ts.

export const KAKAO_JS_KEY = '60b43013bb3a56be45d3175740922838'
export const APPLE_SERVICES_ID = 'com.play-gogo.web'
export const KAKAO_REDIRECT_URI =
  typeof window !== 'undefined'
    ? `${window.location.origin}/auth/kakao/callback`
    : ''
export const APPLE_REDIRECT_URI =
  typeof window !== 'undefined'
    ? `${window.location.origin}/auth/apple/callback`
    : ''

function loadScript(src, checkGlobal) {
  return new Promise((resolve, reject) => {
    if (checkGlobal && checkGlobal()) return resolve()
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', reject)
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`failed to load ${src}`))
    document.head.appendChild(s)
  })
}

let _kakaoReady = null
export function initKakao() {
  if (_kakaoReady) return _kakaoReady
  _kakaoReady = loadScript(
    'https://t1.kakaocdn.net/kakao_js_sdk/2.7.4/kakao.min.js',
    () =>
      window.Kakao &&
      window.Kakao.isInitialized &&
      window.Kakao.isInitialized(),
  ).then(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO_JS_KEY)
    }
    return window.Kakao
  })
  return _kakaoReady
}

// Redirect the browser to Kakao's consent screen. Returns to KAKAO_REDIRECT_URI
// with `?code=...`, handled on load by consumeKakaoRedirect().
export async function startKakaoLogin() {
  await initKakao()
  window.Kakao.Auth.authorize({ redirectUri: KAKAO_REDIRECT_URI })
}

// Call on app load. If the current URL is the Kakao callback with a code,
// returns { code, redirectUri }; otherwise null. The caller exchanges the code
// via the backend (authenticateKakaoWeb) and clears the URL.
// 모듈 로드 시점에 URL 스냅샷 — React Navigation 의 웹 linking 이 히스토리를
// 동기화하며 URL 을 바꾸기 전에 카카오 콜백 code 를 보존한다.
const _initialLocation =
  typeof window !== 'undefined'
    ? { pathname: window.location.pathname, search: window.location.search }
    : null

export function consumeKakaoRedirect() {
  if (!_initialLocation) return null
  if (!_initialLocation.pathname.startsWith('/auth/kakao/callback')) return null
  const code = new URLSearchParams(_initialLocation.search).get('code')
  if (!code) return null
  return { code, redirectUri: KAKAO_REDIRECT_URI }
}

let _appleReady = null
export function initApple() {
  if (_appleReady) return _appleReady
  _appleReady = loadScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    () => window.AppleID,
  ).then(() => {
    window.AppleID.auth.init({
      clientId: APPLE_SERVICES_ID,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    })
    return window.AppleID
  })
  return _appleReady
}

// Popup flow — resolves with the Apple identity token (JWT), the same kind the
// native SDK yields, so the existing /users/authenticate/apple endpoint applies.
export async function appleSignIn() {
  await initApple()
  const res = await window.AppleID.auth.signIn()
  return res && res.authorization && res.authorization.id_token
}
